import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { PlatformGrowth } from '@/api/platformMetrics/platformMetricsApi.types';
import { ChartCard } from '@/components/common/charts/ChartCard';
import { TrendAreaChart, type TrendAreaSeries } from '@/components/common/charts/TrendAreaChart';
import { usePlatformGrowthQuery } from '@/hooks/usePlatformChartsQueries';
import { resolveSeriesColor } from '@/theme/chartPalette';
import { formatDateToMonthAndYear } from '@/utils/dateFormatting';

interface GrowthTrendChartProps {
  isEnabled: boolean;
}

interface CumulativeGrowthRow extends Record<string, string | number> {
  period: string;
  workspaces: number;
  accounts: number;
}

export function GrowthTrendChart({ isEnabled }: GrowthTrendChartProps) {
  const { t } = useTranslation('admin');
  const growthQuery = usePlatformGrowthQuery(isEnabled);
  const rows = growthQuery.data === undefined ? [] : buildCumulativeRows(growthQuery.data);
  const lastRow = rows[rows.length - 1];
  const isEmpty =
    lastRow === undefined || (lastRow.workspaces === 0 && lastRow.accounts === 0);

  const series: TrendAreaSeries[] = [
    {
      key: 'workspaces',
      label: t('dashboard.charts.growth.workspacesSeries'),
      color: resolveSeriesColor(0),
    },
    {
      key: 'accounts',
      label: t('dashboard.charts.growth.accountsSeries'),
      color: resolveSeriesColor(1),
    },
  ];

  return (
    <ChartCard
      title={t('dashboard.charts.growth.title')}
      iconName="TrendingUp"
      isLoading={growthQuery.isLoading === true}
      isError={growthQuery.isError === true}
      onRetry={function retry() {
        growthQuery.refetch();
      }}
      isEmpty={isEmpty}
      emptyLabel={t('dashboard.charts.growth.empty')}
    >
      <TrendAreaChart
        data={rows}
        xKey="period"
        series={series}
        accessibleLabel={t('dashboard.charts.growth.title')}
        formatXTick={formatPeriodLabel}
        formatValue={formatWholeNumber}
      />
    </ChartCard>
  );
}

function buildCumulativeRows(growth: PlatformGrowth): CumulativeGrowthRow[] {
  const rows: CumulativeGrowthRow[] = [];
  let cumulativeWorkspaces = growth.baselineWorkspaces;
  let cumulativeAccounts = growth.baselineAccounts;
  for (const period of growth.periods) {
    cumulativeWorkspaces = cumulativeWorkspaces + period.newWorkspaces;
    cumulativeAccounts = cumulativeAccounts + period.newAccounts;
    rows.push({
      period: period.period,
      workspaces: cumulativeWorkspaces,
      accounts: cumulativeAccounts,
    });
  }
  return rows;
}

function formatPeriodLabel(period: string): string {
  return formatDateToMonthAndYear(parseISO(period + '-01'));
}

function formatWholeNumber(value: number): string {
  return String(value);
}
